"use client";

import React from "react";
import CurrencyFormatter from "../../utils/currencyFormatter";
import { FormattedDate } from "../../utils/dateFormatter";

interface TransactionDetailsProps {
  amount?: number | string | null;
  asset?: string | null;
  currency?: string | null;
  fees?: number | string | null;
  transactionId?: string | null;
  transactionRef?: string | null;
  timestamp?: string | Date | null;
  status?: "complete" | "pending" | "awaiting settlement" | null;
  showFees?: boolean;
  className?: string;
}

export const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  amount,
  asset,
  currency,
  fees,
  transactionId,
  transactionRef,
  timestamp,
  status,
  showFees = true,
  className = "",
}) => {
  const formatter = new CurrencyFormatter();
  const dateFormatter = new FormattedDate();

  const formatAmount = () => {
    if (!amount) return "N/A";
    const assetType =
      asset && asset.toUpperCase() !== "ZAR" ? "crypto" : "fiat";
    const assetSymbol =
      asset?.toUpperCase() || currency?.toUpperCase() || "ZAR";
    return formatter.format(
      typeof amount === "string" ? parseFloat(amount) : amount,
      assetType,
      assetSymbol
    );
  };

  const formatFees = () => {
    if (!fees || !showFees) return null;
    const feeAmount = typeof fees === "string" ? parseFloat(fees) : fees;
    if (feeAmount === 0) return "R 0.00";
    return formatter.format(feeAmount, "fiat", "ZAR");
  };

  const formatTimestamp = () => {
    if (!timestamp) return "N/A";
    const date =
      typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    return dateFormatter.simpleFormat(date);
  };

  const displayTransactionId = transactionId || transactionRef || "N/A";

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="border rounded-lg divide-y bg-gray-50">
        {amount !== null && amount !== undefined && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-sm text-gray-600">Amount</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatAmount()}
            </span>
          </div>
        )}

        {asset && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-sm text-gray-600">Asset</span>
            <span className="text-sm font-semibold text-gray-900">
              {asset.toUpperCase()}
            </span>
          </div>
        )}

        {showFees && fees !== null && fees !== undefined && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-sm text-gray-600">Fees</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatFees() || "R 0.00"}
            </span>
          </div>
        )}

        {displayTransactionId !== "N/A" && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-sm text-gray-600">Transaction ID</span>
            <span className="text-sm font-semibold text-gray-900 break-all text-right max-w-[200px]">
              {displayTransactionId}
            </span>
          </div>
        )}

        {timestamp && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-sm text-gray-600">Timestamp</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatTimestamp()}
            </span>
          </div>
        )}

        {status && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-sm text-gray-600">Status</span>
            <span
              className={`text-xs font-bold px-2 py-1 rounded-full ${
                status === "complete" || status === "completed"
                  ? "bg-[#E8F9F6] text-[#17C3A5]"
                  : status === "pending" || status === "awaiting settlement"
                    ? "bg-[#EFF1FC] text-[#6375E6]"
                    : "bg-[#FFEFD5] text-[#FFC107]"
              }`}
            >
              {status.toUpperCase()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
